import { useCallback, useState } from 'react'

import { Item } from '@inno-sdks/files'
import { toast } from '@inno-ui/toast'
import { useTranslation } from 'react-i18next'

import { useRenameItemMutation } from '../../../api/hooks/useRenameItem'
import { useTrashItemsMutation } from '../../../api/hooks/useTrashItems'
import { maxItemNameLength } from '../../../constants'
import { validateItemName } from '../../../utils'
import { useItemModal } from './useItemModal'

export const useItemModalOperations = (item?: Item) => {
  const { close } = useItemModal()
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.shortcut_item.context_menu'
  })
  const renameItemMutation = useRenameItemMutation()
  const { mutate: trashItems } = useTrashItemsMutation()

  const [modalResetKey, setModalResetKey] = useState(0)

  const handleTitleChange = useCallback(
    (title: string) => {
      if (!item) {
        return
      }

      const nameToUpdate = title.trim().slice(0, maxItemNameLength)

      if (item.name === nameToUpdate) {
        return
      }

      const invalidName = validateItemName(nameToUpdate)

      if (!invalidName) {
        const asyncHandler = async () => {
          await renameItemMutation.mutateAsync({
            id: item.id,
            name: nameToUpdate
          })
        }

        toast.info({
          asyncHandler: asyncHandler(),
          asyncStates: {
            loading: {
              title: t('renaming')
            },
            success: {
              title: t('renamed')
            },
            error: {
              title: t('failed_to_rename')
            }
          }
        })
      } else {
        setModalResetKey(prev => prev + 1)

        toast.dismiss()
        toast.error({
          ...invalidName
        })
      }
    },

    [item, renameItemMutation, t]
  )

  const handleMoveToTrash = useCallback(() => {
    if (!item) {
      return
    }

    close({ force: true })
    trashItems([item.id])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item])

  return {
    handleMoveToTrash,
    handleTitleChange,
    modalResetKey
  }
}
